// src/contexts/NotificationContext.js
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  }, []);

  const addNotification = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const notification = {
      id,
      message,
      type, // 'success', 'error', 'warning', 'info', 'loading'
      duration,
      timestamp: new Date()
    };

    setNotifications(prev => [...prev, notification]);

    // Auto remove after duration (except for loading notifications)
    if (type !== 'loading' && duration > 0) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(notif => notif.id !== id));
      }, duration);
    }

    return id; // Return ID so loading notifications can be updated
  }, []); // No dependencies needed since we use functional updates

  const updateNotification = useCallback((id, updates) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === id ? { ...notif, ...updates } : notif
      )
    );
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Convenience methods - memoized to prevent recreation
  const success = useCallback((message, duration) => 
    addNotification(message, 'success', duration), [addNotification]);
  
  const error = useCallback((message, duration) => 
    addNotification(message, 'error', duration), [addNotification]);
  
  const warning = useCallback((message, duration) => 
    addNotification(message, 'warning', duration), [addNotification]);
  
  const info = useCallback((message, duration) => 
    addNotification(message, 'info', duration), [addNotification]);
  
  const loading = useCallback((message) => 
    addNotification(message, 'loading', 0), [addNotification]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    notifications,
    addNotification,
    removeNotification,
    updateNotification,
    clearAll,
    success,
    error,
    warning,
    info,
    loading
  }), [
    notifications,
    addNotification,
    removeNotification,
    updateNotification,
    clearAll,
    success,
    error,
    warning,
    info,
    loading
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};